import {
  USER_ROLES,
  useRoles,
  useBankForBanksIndication,
} from "@rshbintech.dboul/access-control";
import { useCallback } from "react";
import { BANK_FOR_BANKS_ROUTES } from "./constants";

export function useProtectedRoutes() {
  const { hasAnyRoleThatContains, hasRoleThatContains, hasRole } = useRoles();
  const { isBankForBanksUser } = useBankForBanksIndication();

  const validateRouteForBankForBanksUser = useCallback(
    (routePath: string, strict = true) => {
      const availableRoutes = isBankForBanksUser ? BANK_FOR_BANKS_ROUTES : null;

      if (!availableRoutes) {
        return true;
      }

      return availableRoutes.some((route) =>
        strict
          ? !!routePath && route === routePath
          : !!routePath && route.includes(routePath)
      );
    },
    [isBankForBanksUser]
  );

  const validateRoute = useCallback(
    (path: string): boolean => {
      const formattedPath = path.startsWith("/") ? path : `/${path}`;

      switch (formattedPath) {
        case "/main": {
          return hasAnyRoleThatContains([
            USER_ROLES.VISITOR,
            USER_ROLES.CURRENCY,
            USER_ROLES.currency,
            USER_ROLES.RESPONSIBLE,
            USER_ROLES.OWB_MS_PRD_INVOICES,
            USER_ROLES.FINANCE_ACCOUNT_INFO,
            USER_ROLES.OWB_MS_PRD_RKO_ORDERS,
          ]);
        }

        case "/main/accounts": {
          return hasRoleThatContains(USER_ROLES.FINANCE_ACCOUNT_INFO);
        }

        case "/notifications": {
          return hasRoleThatContains(USER_ROLES.UI_DBO_NOTIFICATION_VIEW);
        }

        case "/payments/templates": {
          return hasRoleThatContains(USER_ROLES.UI_PAYMENT_VISUAL_VIEW);
        }

        case "/counterparties": {
          return hasRoleThatContains(USER_ROLES.CONTRACTOR);
        }

        case "/swift-messages": {
          return hasRoleThatContains(USER_ROLES.SWIFT_MESSAGE_CLIENT);
        }

        case "/invoices": {
          return hasRoleThatContains(USER_ROLES.OWB_MS_PRD_INVOICES);
        }

        case "/statements": {
          return hasRoleThatContains(USER_ROLES.FINANCE_ACCOUNT_INFO);
        }

        case "/rck": {
          return hasAnyRoleThatContains([
            USER_ROLES.RU_PAYMENTS_CONTROL,
            USER_ROLES.HOLDING_ACCOUNTS,
          ]);
        }

        case "/analytics": {
          return hasRoleThatContains(USER_ROLES.FINANCE_ACCOUNT_INFO);
        }

        case "/tariffs": {
          return hasRoleThatContains(USER_ROLES.TARIFFS_TARIFF_ORDER);
        }

        case "/references": {
          return hasRoleThatContains(USER_ROLES.OWB_MS_PRD_RKO_ORDERS);
        }

        case "/showcase/orders": {
          return hasRoleThatContains(USER_ROLES.OWB_MS_PRD_RKO_ORDERS);
        }

        case "/registries": {
          return hasRoleThatContains(USER_ROLES.UI_PAYMENT_VISUAL_VIEW);
        }

        case "/ausn": {
          return hasRoleThatContains(USER_ROLES.UI_CONNECTOR_AUSN_VIEW);
        }

        case "/sbp": {
          return (
            hasAnyRoleThatContains([USER_ROLES.RESPONSIBLE_FOR_LEGAL]) ||
            hasRole(USER_ROLES.VISITOR)
          );
        }

        case "/salary": {
          return hasRoleThatContains(USER_ROLES.OWB_MS_PRD_SALARY);
        }

        case "/cards": {
          return hasRoleThatContains(USER_ROLES.BUSINESS_CARD);
        }

        case "/deposits-and-promissory-notes": {
          return hasAnyRoleThatContains([
            USER_ROLES.DEPOSIT,
            USER_ROLES.PROMISSORY_NOTE,
          ]);
        }

        case "/deposits": {
          return hasRoleThatContains(USER_ROLES.DEPOSIT);
        }

        case "/promissory-notes": {
          return hasRoleThatContains(USER_ROLES.PROMISSORY_NOTE);
        }

        case "/acquiring": {
          return (
            hasAnyRoleThatContains([
              USER_ROLES.RESPONSIBLE_FOR_LEGAL,
              USER_ROLES.RESPONSIBLE_FOR_MERCHANT,
            ]) || hasRole(USER_ROLES.VISITOR)
          );
        }

        case "/foreign-activity": {
          return hasAnyRoleThatContains([
            USER_ROLES.CURRENCY,
            USER_ROLES.currency,
            USER_ROLES.CERT_CURR_TRANS,
          ]);
        }

        case "/loans": {
          return hasRoleThatContains(USER_ROLES.CREDIT_APPLICATION);
        }

        case "/showcase": {
          return hasRoleThatContains(USER_ROLES.SHOWCASE_VIEWER);
        }

        case "/loyalty-program": {
          return hasRoleThatContains(USER_ROLES.LOYALTY_PROGRAM);
        }

        case "/nsd-certificate": {
          return hasRoleThatContains(USER_ROLES.OWB_MS_PRD_MS_H2H);
        }

        case "/cash-management/espp": {
          return hasRoleThatContains(USER_ROLES.CM_ESPP_REGISTER_ACT);
        }

        case "/cash-management/escrow-report": {
          return hasRoleThatContains(USER_ROLES.CM_ESCROW_REPORT_ACT);
        }

        case "/messages": {
          return hasAnyRoleThatContains([
            USER_ROLES.MAIL_BANK,
            USER_ROLES.owb_ms_prd_mail,
          ]);
        }

        case "/rck/signing": {
          return hasRoleThatContains(USER_ROLES.RU_PAYMENTS_CONTROL);
        }

        case "/rck/group-balance": {
          return hasRoleThatContains(USER_ROLES.HOLDING_ACCOUNTS);
        }

        case "/brokerage-services": {
          return hasRoleThatContains(
            USER_ROLES.OWB_MS_PRD_BROKERAGE_CLIENT_WITH_SIGN
          );
        }

        case "/accreditivs": {
          return hasAnyRoleThatContains([
            USER_ROLES.OWB_MS_PRD_ACCREDITIVS_CLIENT_NO_SIGN,
            USER_ROLES.OWB_MS_PRD_ACCREDITIVS_CLIENT_SIGN,
          ]);
        }

        default: {
          return true;
        }
      }
    },
    [hasAnyRoleThatContains, hasRoleThatContains, hasRole]
  );

  return {
    validateRoute,
    validateRouteForBankForBanksUser,
  };
}
